import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    AnimatePresence,
    LayoutGroup,
    motion,
    MotionConfig,
} from 'framer-motion';
import { SwiperClass } from 'swiper/react';
import useResizeObserver from 'use-resize-observer';

import { updateSlideIndex } from 'Scripts/Slices/productDetailPage';
import { IRootState } from 'Scripts/store';

import { dpr } from 'Utils/dpr';
import { getMoveDirection } from 'Utils/getMoveDirection';
import tailwindBreakpoints from 'Utils/tailwindBreakpoints';

import {
    IImageBaseViewModel,
    IProductItemViewModel,
    IProductPageViewModel,
    IVariantImageViewModel,
} from 'Types/Litium';
import { AnimateChangeInHeight } from 'Pages/ProductDetailPage/AnimateChangeInHeight';
import ProductGalleryImage from 'Pages/ProductDetailPage/ProductGalleryImage';
import ProductImageSlide from 'Pages/ProductDetailPage/ProductImageSlide';

import { ProductDetailPageGalleryView } from './ProductDetailPageGalleryView';
import { ProductDetailPageMainSlider } from './ProductDetailPageMainSlider';

const MotionProductGalleryImage = motion.create(ProductGalleryImage);

const MotionProductImageSlide = motion.create(ProductImageSlide);

export interface ISlideModel extends IImageBaseViewModel {
    videoUrl?: string;
    id: string;
}

const ProductGalleryContainer = ({
    slides,
    thumbnails,
    translations,
    videoUrl,
}: {
    videoUrl: IProductItemViewModel['videoUrl'];
    slides: ISlideModel[];
    thumbnails: IVariantImageViewModel[];
    translations: IProductPageViewModel['translations'];
}) => {
    const srcSetSizesOneInRow = {
        default: { width: 368, height: 521 },
        lg: { width: 440, height: 624 },
        xl: {
            width: 648,
            height: 919,
        },
        xxl: {
            width: 994,
            height: 1409,
        },
    };

    const srcSetSizesTwoInRow = {
        default: { width: 314, height: 445 },
        lg: { width: 314, height: 445 },
        xl: {
            width: 434,
            height: 616,
        },
        xxl: {
            width: 462,
            height: 655,
        },
    };

    const srcSetSizesThreeInRow = {
        default: { width: 201, height: 285 },
        lg: { width: 201, height: 285 },
        xl: {
            width: 281,
            height: 398,
        },
        xxl: {
            width: 300,
            height: 425,
        },
    };

    const srcSetSizesSixInRow = {
        default: { width: 89, height: 126 },
        lg: { width: 89, height: 126 },
        xl: {
            width: 129,
            height: 182,
        },
        xxl: {
            width: 138,
            height: 195,
        },
    };

    const maxAmountOfThumbnails = 5;
    const amountOfSlides = slides?.length;

    const animationDuration = 0.3;
    const animationDurationReset = 0;
    const swiperRef = useRef<SwiperClass | null>(null);
    const [, setSignal] = useState({});
    const forceRerender = () => setSignal({});
    const [swiperMode, setSwiperMode] = useState(false);
    const [sliderActive, setSliderActive] = useState(false);
    const [canClose, setCanClose] = useState(false);
    const [currentSliderIndex, setCurrentSliderIndex] = useState(0);

    const [canInteract, setCanInteract] = useState(true);

    const [selectedSwiperSlide, setSelectedSwiperSlide] = useState<
        string | null
    >(slides[0].id);

    const slideIndex = useSelector(
        (state: IRootState) => state?.productDetailPage.slideIndex
    );

    const { ref: galleryRef, height: galleryHeight } =
        useResizeObserver<HTMLDivElement>({ round: Math.ceil });

    useEffect(() => {
        if (
            swiperRef.current &&
            !swiperRef?.current?.destroyed &&
            slideIndex !== null
        ) {
            dispatch(updateSlideIndex(null));
            const swiper = swiperRef?.current;
            if (swiper.realIndex === slideIndex) return;
            const moveDirection = getMoveDirection(
                swiper.realIndex,
                slideIndex,
                swiper.slides.length
            );

            if (moveDirection === 'next') {
                swiper.slideNext();
            } else if (moveDirection === 'previous') {
                swiper.slidePrev();
            } else {
                swiper.slideToLoop(slideIndex);
            }
        }
    }, [slideIndex]);

    useEffect(() => {
        const handleEscapeKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                if (sliderActive) {
                    resetSlideAndClose();
                }
            }
        };
        if (swiperRef.current) {
            swiperRef.current.on('slideChange', forceRerender);
        }
        window.addEventListener('keydown', handleEscapeKey);
        return () => {
            if (swiperRef.current) {
                swiperRef.current.off('slideChange', forceRerender);
            }
            window.removeEventListener('keydown', handleEscapeKey);
        };
    }, [sliderActive]);

    const dispatch = useDispatch();

    const triggerSwiperMode = (index: string) => {
        if (canInteract) {
            const slide = slides.find(x => x.id === index)?.id ?? slides[0].id;

            setSelectedSwiperSlide(slide);
            setTimeout(() => {
                setCurrentSliderIndex(null);
                setSliderActive(false);
                setCanInteract(false);
                setSwiperMode(true);
            }, 1);
        }
    };

    useEffect(() => {
        if (canClose) stopSwiperMode();
    }, [canClose]);

    const resetSlideAndClose = () => {
        if (canInteract) {
            const findIndex = slides.findIndex(
                y => y.id === selectedSwiperSlide
            );
            const indexOrFirst = findIndex >= 0 ? findIndex : 0;

            if (findIndex === -1) {
                swiperRef.current.slideToLoop(indexOrFirst, 300);
                setTimeout(() => {
                    setSliderActive(false);
                    setCanClose(true);
                }, 300);
                setTimeout(() => {}, 300);
            } else {
                setSliderActive(false);
                setCanClose(true);
            }
        }
    };

    const stopSwiperMode = () => {
        setSliderActive(false);
        setCurrentSliderIndex(null);
        setCanInteract(false);
        setSwiperMode(false);
        setCanClose(false);
    };

    useEffect(() => {
        if (swiperRef.current) {
            swiperRef.current.on('slideChange', forceRerender);

            return () => {
                swiperRef.current.off('slideChange', forceRerender);
            };
        }
    }, [swiperRef.current]);

    const updateSwiperSlideIndex = (id: string) => {
        setSelectedSwiperSlide(id);
    };

    const renderProductImages = () => {
        switch (amountOfSlides) {
            case 1:
                return (
                    <figure className="h-full w-full ">
                        <picture>
                            <source
                                width={srcSetSizesOneInRow.xxl.width}
                                height={srcSetSizesOneInRow.xxl.height}
                                media={`(min-width: ${tailwindBreakpoints.xl})`}
                                srcSet={dpr(
                                    `${slides[0].src}?format=webp&fit=crop&height=${srcSetSizesOneInRow.xxl.height}&width=${srcSetSizesOneInRow.xxl.width}`
                                )}
                            />
                            <source
                                width={srcSetSizesOneInRow.xl.width}
                                height={srcSetSizesOneInRow.xl.height}
                                media={`(min-width: ${tailwindBreakpoints.lg})`}
                                srcSet={dpr(
                                    `${slides[0].src}?format=webp&fit=crop&height=${srcSetSizesOneInRow.xl.height}&width=${srcSetSizesOneInRow.xl.width}`
                                )}
                            />
                            <img
                                className="h-full w-full object-cover"
                                src={`${slides[0].src}?format=webp&fit=crop&height=${srcSetSizesOneInRow.default.height}&width=${srcSetSizesOneInRow.default.width}`}
                                srcSet={dpr(
                                    `${slides[0].src}?format=webp&fit=crop&height=${srcSetSizesOneInRow.default.height}&width=${srcSetSizesOneInRow.default.width}`
                                )}
                                alt={slides[0]?.altText}
                                width={srcSetSizesOneInRow.default.width}
                                height={srcSetSizesOneInRow.default.height}
                                loading="eager"
                            />
                        </picture>
                    </figure>
                );
            case 2:
                return (
                    <ProductDetailPageMainSlider
                        animationDuration={animationDuration}
                        sliderActive={sliderActive}
                        animationDurationReset={animationDurationReset}
                        selectedSwiperSlide={selectedSwiperSlide}
                        slides={slides}
                        MotionProductImageSlide={MotionProductImageSlide}
                        swiperRef={swiperRef}
                        translations={translations}
                        videoUrl={videoUrl}
                        thumbnails={thumbnails}
                        galleryHeight={galleryHeight}
                        setSliderActive={() => setSliderActive(true)}
                        setCanInteract={() => setCanInteract(true)}
                        setSelectedSwiperSlide={() => setSelectedSwiperSlide}
                        forceRerender={() => forceRerender()}
                        resetSlideAndClose={resetSlideAndClose}
                        canInteract={canInteract}
                        amountOfSlidesIsTwo={amountOfSlides === 2}
                        amountOfSlides={amountOfSlides}
                        currentSliderIndex={currentSliderIndex}
                    />
                );
            default:
                return (
                    <>
                        <ProductDetailPageGalleryView
                            amountOfSlides={amountOfSlides}
                            galleryRef={galleryRef}
                            slides={slides}
                            MotionProductGalleryImage={
                                MotionProductGalleryImage
                            }
                            translations={translations}
                            triggerSwiperMode={triggerSwiperMode}
                            setCanInteract={() => setCanInteract(true)}
                            srcSetSizesTwoInRow={srcSetSizesTwoInRow}
                            srcSetSizesThreeInRow={srcSetSizesThreeInRow}
                            srcSetSizesSixInRow={srcSetSizesSixInRow}
                            swiperMode={swiperMode}
                            sliderActive={sliderActive}
                            animationDurationReset={animationDurationReset}
                            animationDuration={animationDuration}
                            maxAmountOfThumbnails={maxAmountOfThumbnails}
                        />

                        {swiperMode && (
                            <ProductDetailPageMainSlider
                                animationDuration={animationDuration}
                                sliderActive={sliderActive}
                                animationDurationReset={animationDurationReset}
                                selectedSwiperSlide={selectedSwiperSlide}
                                slides={slides}
                                MotionProductImageSlide={
                                    MotionProductImageSlide
                                }
                                swiperRef={swiperRef}
                                translations={translations}
                                videoUrl={videoUrl}
                                thumbnails={thumbnails}
                                galleryHeight={galleryHeight}
                                setSliderActive={() => setSliderActive(true)}
                                setCanInteract={() => setCanInteract(true)}
                                setSelectedSwiperSlide={updateSwiperSlideIndex}
                                forceRerender={() => forceRerender()}
                                resetSlideAndClose={resetSlideAndClose}
                                canInteract={canInteract}
                                amountOfSlidesIsTwo={amountOfSlides === 2}
                                amountOfSlides={amountOfSlides}
                                currentSliderIndex={currentSliderIndex}
                            />
                        )}
                    </>
                );
        }
    };

    return (
        <MotionConfig transition={{ duration: animationDuration }}>
            <LayoutGroup>
                <AnimateChangeInHeight className="relative">
                    <AnimatePresence mode="popLayout" initial={false}>
                        {renderProductImages()}
                    </AnimatePresence>
                </AnimateChangeInHeight>
            </LayoutGroup>
        </MotionConfig>
    );
};

export default ProductGalleryContainer;
